/**
 * Choix de la carte à la création d'une partie. Chaque carte montre une
 * vignette de son relief, calculée par la même fonction que le jeu mais en
 * basse résolution (quelques milliers de points, quelques millisecondes),
 * sa taille, ses versants et une courte présentation.
 */
import { useEffect, useRef } from 'preact/hooks';
import { MAP_LIST, createTerrainFunction, lakeLevel, mapSize, type GameMap } from '@snowchain/shared';
import './maps.css';

const THUMB = 88;
const cache = new Map<string, ImageData>();

/** Ombrage du relief vu de haut : neige, forêt sous la limite des arbres, roche, lac, village. */
function reliefImage(map: GameMap): ImageData {
  const known = cache.get(map.id);
  if (known) return known;
  const fn = createTerrainFunction(map.features);
  const water = lakeLevel(map.features, fn);
  const size = mapSize(map.features);
  const half = size / 2;
  const step = size / THUMB;
  const n = THUMB + 2;
  const h = new Float32Array(n * n);
  for (let j = 0; j < n; j++) for (let i = 0; i < n; i++) h[j * n + i] = fn(-half + (i - 0.5) * step, -half + (j - 0.5) * step);
  const img = new ImageData(THUMB, THUMB);
  const v = map.features.village;
  const lake = map.features.lake;
  // Lumière rasante du nord-ouest, comme sur une carte en relief.
  const L = [-0.55, 0.62, -0.56];
  for (let j = 0; j < THUMB; j++) {
    for (let i = 0; i < THUMB; i++) {
      const k = (j + 1) * n + (i + 1);
      const hh = h[k] as number;
      const gx = ((h[k + 1] as number) - (h[k - 1] as number)) / (2 * step);
      const gz = ((h[k + n] as number) - (h[k - n] as number)) / (2 * step);
      const len = Math.hypot(gx, 1, gz);
      const lit = Math.max(0, (-gx * (L[0] as number) + (L[1] as number) - gz * (L[2] as number)) / len);
      const slope = Math.hypot(gx, gz);
      const t = Math.min(1, Math.max(0, 0.05 + lit * 1.2));
      // Neige ombrée de bleu.
      let r = 96 + 156 * t;
      let g = 116 + 137 * t;
      let b = 160 + 92 * t;
      if (hh < map.treeLine && slope < 0.9) {
        // Forêt : vert sombre, plus clairsemée près de la limite des arbres.
        const f = Math.min(1, (map.treeLine - hh) / 90) * 0.62;
        r += (52 + 30 * t - r) * f;
        g += (84 + 34 * t - g) * f;
        b += (70 + 26 * t - b) * f;
      }
      if (slope > 0.75) {
        const k2 = Math.min(1, (slope - 0.75) * 2.5) * 0.7;
        r += (110 * t + 40 - r) * k2;
        g += (108 * t + 40 - g) * k2;
        b += (112 * t + 46 - b) * k2;
      }
      const x = -half + (i + 0.5) * step;
      const z = -half + (j + 0.5) * step;
      if (hh < water && Math.hypot(x - lake.x, z - lake.z) < lake.radius * 1.3) {
        r = 86;
        g = 146;
        b = 190;
      }
      if (Math.hypot(x - v.x, z - v.z) < Math.max(v.radius * 0.55, step * 1.6)) {
        r = 226;
        g = 122;
        b = 64;
      }
      const o = (j * THUMB + i) * 4;
      img.data[o] = r;
      img.data[o + 1] = g;
      img.data[o + 2] = b;
      img.data[o + 3] = 255;
    }
  }
  cache.set(map.id, img);
  return img;
}

function Relief(props: { map: GameMap }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current?.getContext('2d');
    if (c) c.putImageData(reliefImage(props.map), 0, 0);
  }, [props.map.id]);
  return <canvas ref={ref} width={THUMB} height={THUMB} class="mn-map-relief" aria-hidden="true" />;
}

/** Taille lisible : « 2 × 2 km ». */
export function mapSizeLabel(map: GameMap): string {
  const km = Math.round(mapSize(map.features) / 1000);
  return `${km} × ${km} km`;
}

export function mapFacesLabel(map: GameMap): string {
  const n = map.faces.length;
  return n > 1 ? `${n} versants` : '1 versant';
}

export function MapPicker(props: { value: string; onChange: (id: string) => void }) {
  return (
    <div class="mn-maps" role="radiogroup" aria-label="Carte">
      {MAP_LIST.map((m) => {
        const on = props.value === m.id;
        return (
          <button key={m.id} type="button" role="radio" aria-checked={on} class={`mn-map${on ? ' on' : ''}`} onClick={() => props.onChange(m.id)}>
            <Relief map={m} />
            <span class="mn-map-body">
              <span class="mn-map-name">{m.name}</span>
              <span class="mn-map-meta">
                {mapSizeLabel(m)} · {mapFacesLabel(m)}
              </span>
              <span class="mn-map-faces">{m.faces.join(' · ')}</span>
              <span class="mn-map-blurb">{m.blurb}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
